import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { profileApi, api } from '../api/client';
import { processImage } from '../utils/imageProcessing';
import { getAgeFromDob } from '../utils/ageUtils';
import { useTranslation } from '../i18n';
import {
  Camera, Pencil, Handshake, Users, CalendarDays,
  FileText, Check, Loader2, AlertTriangle, LogOut,
  Globe, Bell, ChevronDown, Calendar, HeartHandshake,
  Flame, Trophy, BookMarked, Target, Sparkles, Rocket,
  Sprout, Smartphone, X, Lock, Globe2,
} from 'lucide-react';
import { CrossIcon } from '../components/Icons';
import { PushSettingsToggle } from '../components/PushPrompt';
import StoryCard from './story/StoryCard';
import { ScrollText } from 'lucide-react';
import './ProfileScreen.css';
import CoverPhotoEditor from '../components/CoverPhotoEditor';
import { FaithCheckinProfileBlock, FaithCheckinChart } from './FaithCheckin';
import { EvangelismStats } from './evangelism/EvangelismCheckin';
import DevicesScreen from './DevicesScreen';

function getInitials(n='') { return n.split(' ').map(w=>w[0]).join('').toUpperCase().slice(0,2)||'?'; }

// Common timezones grouped
const TIMEZONES = [
  { label: 'US & Canada', zones: [
    'America/New_York', 'America/Chicago', 'America/Denver',
    'America/Los_Angeles', 'America/Anchorage', 'Pacific/Honolulu',
    'America/Phoenix',
  ]},
  { label: 'Europe', zones: [
    'Europe/London', 'Europe/Paris', 'Europe/Berlin',
    'Europe/Rome', 'Europe/Madrid', 'Europe/Amsterdam',
  ]},
  { label: 'Asia / Pacific', zones: [
    'Asia/Tokyo', 'Asia/Seoul', 'Asia/Shanghai', 'Asia/Hong_Kong',
    'Asia/Singapore', 'Asia/Kolkata', 'Asia/Dubai',
    'Australia/Sydney', 'Pacific/Auckland',
  ]},
  { label: 'Other', zones: [
    'Africa/Johannesburg', 'America/Sao_Paulo', 'America/Toronto',
    'America/Vancouver', 'America/Mexico_City',
  ]},
];
function tzLabel(tz) {
  // Turn 'America/New_York' → 'New York'
  return tz.split('/').pop().replace(/_/g, ' ');
}



function formatJoinDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

export default function ProfileScreen() {
  const { signOut } = useAuth();
  const navigate    = useNavigate();
  const [profile, setProfile]   = useState(null);
  const [stats, setStats]       = useState(null);
  const [stories, setStories]   = useState([]);
  const [statVis, setStatVis]   = useState({});
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [editing, setEditing]   = useState(false);
  const [saving, setSaving]     = useState(false);
  const [showFaithChart, setShowFaithChart] = useState(false);
  const [showDevices, setShowDevices] = useState(false);
  const [avatarLoading, setAvatarLoading]   = useState(false);
  const [coverLoading, setCoverLoading]     = useState(false);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName]   = useState('');
  const [bio, setBio]             = useState('');
  const [verse, setVerse]         = useState('');
  const [username, setUsername]   = useState('');
  const [timezone, setTimezone]   = useState('America/New_York');
  const [notifTime, setNotifTime] = useState('08:00');
  const [language, setLanguagePref] = useState('en');
  const [dob, setDob]             = useState('');
  const [dobError, setDobError]   = useState('');
  const { t, lang, setLang } = useTranslation();
  const [usernameStatus, setUsernameStatus] = useState(null);
  const [usernameMsg, setUsernameMsg]       = useState('');
  const usernameTimer = useRef();
  const avatarRef     = useRef();

  useEffect(() => { loadProfile(); }, []);

  async function loadProfile() {
    setLoading(true); setError('');
    try {
      const d = await profileApi.get();
      setProfile(d.user); setStats(d.stats); setStories(d.stories || []);
      setStatVis(d.user.statVisibility || {});
      setFirstName(d.user.firstName||'');
      setLastName(d.user.lastName||'');
      setBio(d.user.bio||'');
      setVerse(d.user.favoriteVerse||'');
      setUsername(d.user.username||'');
      setTimezone(d.user.timezone||'America/New_York');
      setNotifTime(d.user.prayerNotificationTime||'08:00');
      setDob(d.user.dateOfBirth ? new Date(d.user.dateOfBirth).toISOString().split('T')[0] : '');
      const savedLang = d.user.language || 'en';
      setLanguagePref(savedLang);
      // Sync the app language with what's stored in the profile
      setLang(savedLang);
    } catch { setError('Could not load profile'); }
    finally { setLoading(false); }
  }

  function handleUsernameChange(val) {
    setUsername(val); setUsernameStatus(null); setUsernameMsg('');
    clearTimeout(usernameTimer.current);
    if (!val.trim() || val===profile?.username) return;
    if (!/^[a-zA-Z0-9_.]{3,30}$/.test(val)) {
      setUsernameStatus('invalid'); setUsernameMsg('3–30 chars, letters/numbers/dots/underscores'); return;
    }
    setUsernameStatus('checking');
    usernameTimer.current = setTimeout(async () => {
      try {
        const r = await api.get(`/api/profile/check-username?username=${encodeURIComponent(val)}`);
        setUsernameStatus(r.available?'available':'taken');
        setUsernameMsg(r.available?'Username available':'Already taken');
      } catch { setUsernameStatus(null); }
    }, 500);
  }

  async function handleAvatarChange(e) {
    const file = e.target.files[0]; if (!file) return;
    setAvatarLoading(true);
    try {
      const { blob, type } = await processImage(file, { preset: 'avatar' });
      const { uploadUrl, fileUrl } = await api.post('/api/profile/avatar-presign', { fileType: type });
      await fetch(uploadUrl, { method:'PUT', body: blob, headers:{'Content-Type': type} });
      const d = await profileApi.update({ avatar: fileUrl });
      setProfile(d.user);
    } catch(err) { alert(err.message||'Avatar upload failed'); }
    finally { setAvatarLoading(false); if(avatarRef.current) avatarRef.current.value=''; }
  }

  const STAT_VIS_DEFAULTS = {
    prayerListCount:'public', prayerRequestCount:'public', communitiesJoined:'public', postsShared:'public',
    prayersSaid:'connections', prayerCompletionRate:'connections', currentStreak:'connections',
    bestStreak:'connections', bibleStudies:'connections', growthCompleted:'connections', daysActive:'connections',
    deviceCount:'private', connections:'private',
  };
  const ALWAYS_PRIVATE = new Set(['deviceCount','connections']);
  const VIS_CYCLE = { public:'connections', connections:'private', private:'public' };

  async function cycleStatVis(key) {
    if (ALWAYS_PRIVATE.has(key)) return;
    const current = statVis[key] || STAT_VIS_DEFAULTS[key] || 'public';
    const next = VIS_CYCLE[current] || 'public';
    const updated = { ...statVis, [key]: next };
    setStatVis(updated);
    try { await api.put('/api/profile', { statVisibility: updated }); }
    catch { setStatVis(statVis); } // revert on error
  }

  async function handleCoverSave(file, yPercent) {
    setCoverLoading(true);
    try {
      if (file) {
        // New image selected — upload then save
        const { blob, type } = await processImage(file, { preset: 'cover' });
        const { uploadUrl, fileUrl } = await api.post('/api/profile/cover-presign', { fileType: type });
        await fetch(uploadUrl, { method:'PUT', body: blob, headers:{'Content-Type': type} });
        const d = await profileApi.update({ coverPhoto: fileUrl, coverPhotoPosition: yPercent });
        setProfile(d.user);
      } else {
        // Reposition only — save new position, keep existing URL
        const d = await profileApi.update({ coverPhotoPosition: yPercent });
        setProfile(d.user);
      }
    } catch(err) { alert(err.message||'Cover photo update failed'); }
    finally { setCoverLoading(false); }
  }

  async function handleSave() {
    if (usernameStatus==='taken'||usernameStatus==='invalid') return;
    // Validate DOB if changed
    const originalDob = profile?.dateOfBirth ? new Date(profile.dateOfBirth).toISOString().split('T')[0] : '';
    if (dob !== originalDob && dob) {
      const age = getAgeFromDob(dob);
      if (age !== null && age < 13) {
        setDobError(t('profile.dobUnder13'));
        return;
      }
    }
    setDobError('');
    setSaving(true);
    try {
      const payload = { firstName, lastName, bio, favoriteVerse: verse, timezone, prayerNotificationTime: notifTime, language };
      if (username !== profile?.username) payload.username = username||null;
      if (dob !== originalDob) payload.dateOfBirth = dob || null;
      const d = await profileApi.update(payload);
      setProfile(d.user); setEditing(false);
      // Apply language change immediately
      setLang(language);
    } catch(err) { alert(err.message||'Failed to save'); }
    finally { setSaving(false); }
  }

  function cancelEdit() {
    setFirstName(profile?.firstName||''); setLastName(profile?.lastName||'');
    setBio(profile?.bio||''); setVerse(profile?.favoriteVerse||'');
    setUsername(profile?.username||'');
    setTimezone(profile?.timezone||'America/New_York');
    setNotifTime(profile?.prayerNotificationTime||'08:00');
    setDob(profile?.dateOfBirth ? new Date(profile.dateOfBirth).toISOString().split('T')[0] : '');
    setDobError('');
    setUsernameStatus(null); setUsernameMsg(''); setEditing(false);
  }

  if (showFaithChart) return <FaithCheckinChart onClose={() => setShowFaithChart(false)}/>;
  if (showDevices)    return <DevicesScreen onClose={() => setShowDevices(false)}/>;

  if (loading) return <div style={{display:'flex',justifyContent:'center',padding:80}}><span className="spinner" style={{width:32,height:32}}/></div>;
  if (error)   return <div className="page"><div className="empty-state"><AlertTriangle size={36} strokeWidth={1.5} color="var(--text-lt)"/><h3>Something went wrong</h3><p>{error}</p><button className="btn btn-primary" onClick={loadProfile}>Retry</button></div></div>;

  const prayerRate = stats?.prayerCompletionRate;
  const STAT_ITEMS = [
    // ── Prayer & Faith ──
    { label: t('profile.stats.prayersSaid'),     val: stats?.prayersSaid||0,         visKey:'prayersSaid',
      Icon: HeartHandshake, bg:'#fff7ed', color:'#ea580c', darkBg:'rgba(234,88,12,0.12)' },
    { label: t('profile.stats.prayerRecipients'), val: stats?.prayerListCount||0,      visKey:'prayerListCount',
      Icon: Users,          bg:'#fef9ee', color:'#ca8a04', darkBg:'rgba(202,138,4,0.12)' },
    { label: t('profile.stats.prayerRequests'),   val: stats?.prayerRequestCount||0,   visKey:'prayerRequestCount',
      Icon: BookMarked,     bg:'#eef2ff', color:'#6366f1', darkBg:'rgba(99,102,241,0.12)' },
    { label: t('profile.stats.prayerRate'),
      val: prayerRate !== null && prayerRate !== undefined ? `${prayerRate}%` : '—',    visKey:'prayerCompletionRate',
      Icon: Target,         bg:'#f0fdf4', color:'#16a34a', darkBg:'rgba(22,163,74,0.12)' },
    // ── Streaks ──
    { label: t('profile.stats.currentStreak'),   val: `${stats?.currentStreak||0}d`,   visKey:'currentStreak',
      Icon: Flame,          bg:'#fff7ed', color:'#f97316', darkBg:'rgba(249,115,22,0.12)' },
    { label: t('profile.stats.bestStreak'),      val: `${stats?.bestStreak||0}d`,       visKey:'bestStreak',
      Icon: Trophy,         bg:'#fefce8', color:'#ca8a04', darkBg:'rgba(202,138,4,0.12)' },
    // ── Study & Content ──
    { label: t('profile.stats.bibleStudies'),    val: stats?.bibleStudies||0,           visKey:'bibleStudies',
      Icon: BookMarked,     bg:'#eff6ff', color:'#2563eb', darkBg:'rgba(37,99,235,0.12)' },
    { label: t('profile.stats.growthCompleted'), val: stats?.growthCompleted||0,        visKey:'growthCompleted',
      Icon: Sprout,         bg:'#f0fdf4', color:'#16a34a', darkBg:'rgba(22,163,74,0.12)' },
    { label: t('profile.stats.devices'),           val: stats?.deviceCount||0,           visKey:'deviceCount',
      Icon: Smartphone,     bg:'#f5f3ff', color:'#7c3aed', darkBg:'rgba(124,58,237,0.12)',
      onClick: () => setShowDevices(true) },
    // ── Community ──
    { label: t('profile.stats.communities'),      val: stats?.communitiesJoined||0,    visKey:'communitiesJoined',
      Icon: Users,          bg:'#fdf2f8', color:'#db2777', darkBg:'rgba(219,39,119,0.12)' },
    { label: t('profile.stats.connections'),      val: stats?.connections||0,            visKey:'connections',
      Icon: Handshake,      bg:'#f0f9ff', color:'#0284c7', darkBg:'rgba(2,132,199,0.12)' },
    // ── Activity ──
    { label: t('profile.stats.daysActive'),      val: stats?.daysActive||0,             visKey:'daysActive',
      Icon: CalendarDays,   bg:'#f8fafc', color:'#475569', darkBg:'rgba(71,85,105,0.12)' },
    { label: t('profile.stats.connections'),      val: stats?.connections||0,
      Icon: Sparkles,       bg:'#fdf4ff', color:'#9333ea', darkBg:'rgba(147,51,234,0.12)' },
  ].filter((s, i, arr) => arr.findIndex(x => x.label === s.label) === i); // dedupe
  const usernameColor = usernameStatus==='available'?'var(--teal)':usernameStatus==='taken'||usernameStatus==='invalid'?'var(--red)':'var(--gray-3)';

  return (
    <div className="page profile-page">
      {/* Cover photo + avatar hero */}
      <div className="profile-cover-section card" style={{overflow:'visible',padding:0,marginBottom:0,borderRadius:'var(--radius) var(--radius) 0 0',boxShadow:'none'}}>
        {/* Cover */}
        <CoverPhotoEditor
          currentUrl={profile.coverPhoto}
          currentPosition={profile.coverPhotoPosition ?? 50}
          height={140}
          buttonLabel={t('profile.editCover')}
          loading={coverLoading}
          onSave={handleCoverSave}
        />
        {/* Avatar floated over the cover */}
        <div className="profile-avatar-float">
          {profile.avatar
            ? <img src={profile.avatar} alt={profile.name} className="profile-avatar-img"/>
            : <div className="profile-avatar-initials">{getInitials(profile.name)}</div>}
          <button className="avatar-edit-btn" onClick={()=>avatarRef.current.click()} disabled={avatarLoading}>
            {avatarLoading ? <Loader2 size={12} className="spin"/> : <Camera size={12}/>}
          </button>
          <input ref={avatarRef} type="file" accept="image/*" style={{display:'none'}} onChange={handleAvatarChange}/>
        </div>
      </div>

      {/* Profile info */}
      <div className={`profile-hero card${editing ? ' profile-hero-editing' : ''}`} style={{paddingTop:52,borderRadius:'0 0 var(--radius) var(--radius)',borderTop:'none'}}>
        <div className="profile-hero-info" style={editing ? {flexDirection:'column',width:'100%',maxWidth:'100%'} : {}}>
          {!editing ? (
            <>
              {/* Left: name/bio column */}
              <div className="profile-hero-left">
                <h1 className="profile-name">{profile.name}</h1>
                {profile.username && <span className="profile-username">@{profile.username}</span>}
                {!profile.username && <button className="profile-username-prompt" onClick={()=>setEditing(true)}>+ Set username</button>}
                {profile.createdAt && (
                  <span className="profile-joined">
                    <Calendar size={12}/> {t('profile.joined', formatJoinDate(profile.createdAt))}
                  </span>
                )}
                {profile.bio && <p className="profile-bio">{profile.bio}</p>}
                {profile.favoriteVerse && (
                  <p className="profile-verse">
                    <CrossIcon size={13} color="var(--orange)"/> "{profile.favoriteVerse}"
                  </p>
                )}
              </div>
              {/* Right: action buttons */}
              <div className="profile-hero-right">
                <button className="btn btn-primary" style={{fontSize:12,padding:'7px 14px',whiteSpace:'nowrap'}} onClick={()=>setEditing(true)}>
                  <Pencil size={12}/> {t('profile.edit')}
                </button>
                <button className="btn btn-outline" style={{fontSize:12,padding:'7px 14px',whiteSpace:'nowrap'}} onClick={()=>navigate('/connections')}>
                  <Handshake size={12}/> {t('profile.connections')}
                </button>
              </div>
            </>
          ) : (
            <div className="profile-edit-form" style={{width:'100%'}}>
              <div className="profile-edit-row">
                <div className="input-group">
                  <label>{t('profile.firstName')}</label>
                  <input value={firstName} onChange={e=>setFirstName(e.target.value)} placeholder={t('profile.firstNamePlaceholder')}/>
                </div>
                <div className="input-group">
                  <label>{t('profile.lastName')}</label>
                  <input value={lastName} onChange={e=>setLastName(e.target.value)} placeholder={t('profile.lastNamePlaceholder')}/>
                </div>
              </div>
              <div className="input-group">
                <label>{t('profile.username')}</label>
                <div style={{position:'relative'}}>
                  <span style={{position:'absolute',left:12,top:'50%',transform:'translateY(-50%)',color:'var(--gray-3)'}}>@</span>
                  <input value={username} onChange={e=>handleUsernameChange(e.target.value)} placeholder="yourname" style={{paddingLeft:28}}/>
                </div>
                {usernameMsg && (
                  <span style={{fontSize:12,color:usernameColor,display:'flex',alignItems:'center',gap:4}}>
                    {usernameStatus==='checking' && <Loader2 size={12} className="spin"/>}
                    {usernameStatus==='available' && <Check size={12}/>}
                    {usernameMsg}
                  </span>
                )}
              </div>
              <div className="input-group">
                <label>{t('profile.bio')}</label>
                <textarea value={bio} onChange={e=>setBio(e.target.value)} rows={3} placeholder={t('profile.bioPlaceholder')}/>
              </div>
              <div className="input-group">
                <label>{t('profile.favoriteVerse')}</label>
                <input value={verse} onChange={e=>setVerse(e.target.value)} placeholder={t('profile.versePlaceholder')}/>
              </div>

              {/* Private settings section */}
              <div className="profile-private-section">
                <div className="profile-private-heading">
                  <span>{t('profile.privateSettings')}</span>
                  <span className="profile-private-note">{t('profile.privateNote')}</span>
                </div>
                <div className="input-group">
                  <label>{t('profile.dateOfBirth')}</label>
                  <input
                    type="date"
                    className="profile-dob-input"
                    value={dob}
                    onChange={e => { setDob(e.target.value); setDobError(''); }}
                    max={new Date().toISOString().split('T')[0]}
                  />
                  <span style={{fontSize:11,color:'var(--gray-3)'}}>{t('profile.dobDesc')}</span>
                  {dobError && <span style={{fontSize:12,color:'var(--red)',fontWeight:600}}>{dobError}</span>}
                  {profile?.ageGroup && (
                    <span style={{fontSize:12,color:'var(--teal)',fontWeight:600}}>
                      {t('profile.ageGroupLabel')}: {t('profile.ageGroupDisplay', profile.ageGroup)}
                    </span>
                  )}
                </div>
                <div className="input-group">
                  <label><Globe size={12}/> {t('profile.timezone')}</label>
                  <div className="profile-select-wrap">
                    <select value={timezone} onChange={e=>setTimezone(e.target.value)} className="profile-native-select">
                      {TIMEZONES.map(group => (
                        <optgroup key={group.label} label={group.label}>
                          {group.zones.map(tz => (
                            <option key={tz} value={tz}>{tzLabel(tz)} ({tz.split('/')[0]})</option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                    <ChevronDown size={14} className="profile-select-chevron"/>
                  </div>
                </div>
                <div className="input-group">
                  <label><Bell size={12}/> {t('profile.prayerReminder')}</label>
                  <div className="profile-time-input-wrap">
                    <input
                      type="time"
                      className="profile-time-input"
                      value={notifTime}
                      onChange={e => setNotifTime(e.target.value)}
                    />
                    <button
                      type="button"
                      className="profile-time-clear"
                      onClick={() => setNotifTime('')}
                      title="Remove reminder"
                      style={{display: notifTime ? 'flex' : 'none'}}
                    >
                      <X size={13}/>
                    </button>
                  </div>
                  <span style={{fontSize:11,color:'var(--gray-3)'}}>{t('profile.prayerReminderNote')}</span>
                </div>
                <div className="input-group">
                  <label><Globe size={12}/> {t('profile.language')}</label>
                  <div className="profile-lang-btns">
                    <button
                      type="button"
                      className={`profile-lang-btn${language === 'en' ? ' active' : ''}`}
                      onClick={() => setLanguagePref('en')}>
                      English
                    </button>
                    <button
                      type="button"
                      className={`profile-lang-btn${language === 'es' ? ' active' : ''}`}
                      onClick={() => setLanguagePref('es')}>
                      Español
                    </button>
                  </div>
                  <span style={{fontSize:11,color:'var(--gray-3)'}}>{t('profile.languageNote')}</span>
                </div>
                <PushSettingsToggle />
              </div>

              <div style={{display:'flex',gap:10,marginTop:4}}>
                <button className="btn btn-primary" onClick={handleSave}
                  disabled={saving||usernameStatus==='taken'||usernameStatus==='invalid'} style={{flex:1}}>
                  {saving?<span className="spinner"/>:'Save'}
                </button>
                <button className="btn btn-outline" onClick={cancelEdit} style={{flex:1}}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Faith Check-in block — above stats */}
      <FaithCheckinProfileBlock onOpenChart={() => setShowFaithChart(true)}/>

      {/* Weekly evangelism check-in — streak + patterns */}
      <EvangelismStats />

      {/* Stats */}
      <div className="profile-stats">
        {STAT_ITEMS.map(s => {
          const effectiveVis = s.visKey ? (statVis[s.visKey] || STAT_VIS_DEFAULTS[s.visKey] || 'public') : null;
          const isAlwaysPrivate = s.visKey && ALWAYS_PRIVATE.has(s.visKey);
          const VisIcon = effectiveVis === 'private' ? Lock : effectiveVis === 'connections' ? Users : Globe2;
          const visLabel = isAlwaysPrivate
            ? 'Always private'
            : `Visible to: ${effectiveVis === 'public' ? 'everyone' : effectiveVis === 'connections' ? 'my connections' : 'only me'}. Tap to change.`;
          return (
            <div key={s.label}
              className={`profile-stat card ${s.onClick ? 'clickable' : ''}`}
              style={{background: s.bg, position:'relative'}}
              onClick={s.onClick || undefined}>
              {s.visKey && (
                <button
                  type="button"
                  className={`profile-stat-vis-btn${isAlwaysPrivate ? ' always-private' : ''}`}
                  data-vis={isAlwaysPrivate ? 'locked' : effectiveVis}
                  aria-label={visLabel}
                  title={visLabel}
                  disabled={isAlwaysPrivate}
                  onClick={(e) => { e.stopPropagation(); if (!isAlwaysPrivate) cycleStatVis(s.visKey); }}>
                  <VisIcon size={16} strokeWidth={2.5}/>
                </button>
              )}
              <div className="profile-stat-icon" style={{background: s.darkBg || s.bg, color: s.color}}>
                <s.Icon size={17} strokeWidth={2}/>
              </div>
              <span className="profile-stat-val" style={{color: s.color}}>{s.val}</span>
              <span className="profile-stat-label">{s.label}</span>
            </div>
          );
        })}
      </div>

      {/* My Stories */}
      <div className="profile-stories">
        <h3 className="profile-stories-head">
          {t('testimony.profileSectionOwn')}
          <button className="profile-stories-manage" onClick={() => navigate('/stories?tab=mine')}>{t('testimony.manageStories')}</button>
        </h3>
        {stories.length === 0 ? (
          <button className="profile-stories-empty" onClick={() => navigate('/stories?tab=mine&create=1')}>
            <ScrollText size={18}/> {t('testimony.emptyCta')}
          </button>
        ) : (
          <div className="ms-grid">
            {stories.map(s => (
              <StoryCard key={s.id} story={s} onOpen={() => navigate(`/testimony/${s.id}`)} />
            ))}
          </div>
        )}
      </div>

      {/* ── Two-column lower section: Highlights + Quick Actions ── */}
      <div className="profile-lower-grid">

          {/* Spiritual Highlights */}
          {stats && (
            <div className="profile-highlights card">
<h3 className="profile-highlights-title">{t('profile.highlights')}</h3>
              <div className="profile-hl-list">
                {stats.currentStreak > 0 && (
                  <div className="profile-hl-row">
                    <span className="profile-hl-icon" style={{background:'#fff7ed',color:'#ea580c'}}>🔥</span>
                    <div className="profile-hl-body">
                      <span className="profile-hl-val">{stats.currentStreak}-day streak</span>
                      <span className="profile-hl-sub">Prayer calendar</span>
                    </div>
                  </div>
                )}
                {stats.prayerCompletionRate !== null && stats.prayerCompletionRate !== undefined && (
                  <div className="profile-hl-row">
                    <span className="profile-hl-icon" style={{background:'#fdf2f8',color:'#be185d'}}>🙏</span>
                    <div className="profile-hl-body">
                      <span className="profile-hl-val">{stats.prayerCompletionRate}% prayer rate</span>
                      <div className="profile-hl-bar-track">
                        <div className="profile-hl-bar-fill" style={{width:`${stats.prayerCompletionRate}%`,background:'#be185d'}}/>
                      </div>
                    </div>
                  </div>
                )}
                {stats.growthCompleted > 0 && (
                  <div className="profile-hl-row">
                    <span className="profile-hl-icon" style={{background:'#f0fdf4',color:'#16a34a'}}>🌱</span>
                    <div className="profile-hl-body">
                      <span className="profile-hl-val">{t('profile.planCount', stats.growthCompleted)}</span>
                      <span className="profile-hl-sub">{t('profile.hlSub.growthCompleted')}</span>
                    </div>
                  </div>
                )}
                {stats.bibleStudies > 0 && (
                  <div className="profile-hl-row">
                    <span className="profile-hl-icon" style={{background:'#eff6ff',color:'#2563eb'}}>📖</span>
                    <div className="profile-hl-body">
                      <span className="profile-hl-val">{t('profile.bibleCount', stats.bibleStudies)}</span>
                      <span className="profile-hl-sub">{t('profile.hlSub.bibleStudies')}</span>
                    </div>
                  </div>
                )}
                <div className="profile-hl-row">
                    <span className="profile-hl-icon" style={{background:'#fff7ed',color:'#ea580c'}}>🕊️</span>
                    <div className="profile-hl-body">
                      <span className="profile-hl-val">{t('profile.prayNowSessions', stats.prayNowSessions||0)}</span>
                      <span className="profile-hl-sub">{t('profile.hlSub.prayNowSessions', stats.prayNowSessions||0)}</span>
                    </div>
                  </div>
                <div className="profile-hl-row">
                  <span className="profile-hl-icon" style={{background:'#f8fafc',color:'#475569'}}>📅</span>
                  <div className="profile-hl-body">
                    <span className="profile-hl-val">{stats.daysActive} days with Ignite</span>
                    <span className="profile-hl-sub">{t('profile.hlSub.daysActive')}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Quick Actions */}
          <div className="profile-quick-actions card">
<h3 className="profile-highlights-title">{t('profile.quickActions')}</h3>
            <div className="profile-qa-list">
              <button className="profile-qa-btn" onClick={() => navigate('/pray-now')}>
                <span className="profile-qa-icon" style={{background:'#fff7ed',color:'#ea580c'}}>🕊️</span>
<div><div className="profile-qa-label">{t('profile.qaLabels.prayNow')}</div><div className="profile-qa-sub">{t('profile.qaLabels.prayNowSub')}</div></div>
              </button>
              <button className="profile-qa-btn" onClick={() => navigate('/prayer/requests')}>
                <span className="profile-qa-icon" style={{background:'#fdf2f8',color:'#be185d'}}>📌</span>
<div><div className="profile-qa-label">{t('profile.qaLabels.addPrayer')}</div><div className="profile-qa-sub">{t('profile.qaLabels.addPrayerSub')}</div></div>
              </button>
              <button className="profile-qa-btn" onClick={() => navigate('/bible-study')}>
                <span className="profile-qa-icon" style={{background:'#eff6ff',color:'#2563eb'}}>📖</span>
<div><div className="profile-qa-label">{t('profile.qaLabels.bibleStudy')}</div><div className="profile-qa-sub">{t('profile.qaLabels.bibleStudySub')}</div></div>
              </button>
              <button className="profile-qa-btn" onClick={() => navigate('/grow')}>
                <span className="profile-qa-icon" style={{background:'#f0fdf4',color:'#16a34a'}}>🌱</span>
<div><div className="profile-qa-label">{t('profile.qaLabels.growDeeper')}</div><div className="profile-qa-sub">{t('profile.qaLabels.growDeeperSub')}</div></div>
              </button>
            </div>
          </div>

      </div>{/* /profile-lower-grid */}

      {/* Getting Started */}
      <button className="profile-getting-started" style={{width:'100%'}} onClick={() => navigate('/getting-started')}>
<Rocket size={16}/> {t('profile.gettingStartedGuide')}
      </button>

      <button className="profile-signout" onClick={signOut}>
<LogOut size={16}/> {t('profile.signOut')}
      </button>
    </div>
  );
}
